import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Banknote, Eye, Lock, Wallet, ArrowDownUp, Send, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useResourceList, useResourceMutations } from "@/hooks/useResource";
import { useOptions } from "@/hooks/useOptions";
import { PageHeader } from "@/components/kit/PageHeader";
import { StatCard } from "@/components/kit/StatCard";
import { DataTablePro } from "@/components/kit/DataTablePro";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { Button } from "@/components/ui/button";
import { fmtMoney, fmtDate } from "@/lib/format";
import { userOptions } from "@/lib/users";
import { cn } from "@/lib/utils";
import { WalletDrawer } from "@/drawers/WalletDrawer";
import { WithdrawalDrawer } from "@/drawers/WithdrawalDrawer";
import { WithdrawFlow } from "@/drawers/WithdrawFlow";
import { DateRangePicker } from "@/components/kit/DateRangePicker";

const asOpts = (arr) => (arr || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));

export default function Finance() {
  const { user } = useAuth();
  const opts = useOptions();
  const staff = ["manager", "tenant_admin", "super_admin"].includes(user.role);
  const [tab, setTab] = useState("Payouts");
  const [walletDrawer, setWalletDrawer] = useState({ open: false, item: null });
  const [wdOpen, setWdOpen] = useState(false);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [review, setReview] = useState(null);

  const { data: wallets, isLoading: wLoading } = useResourceList("wallets", {});
  const { data: withdrawals, isLoading: wdLoading } = useResourceList("withdrawals", {});
  const { data: bal } = useQuery({ queryKey: ["balances", "me"], queryFn: async () => (await http.get("/conversions/balances")).data });
  const { remove } = useResourceMutations("wallets");
  const qc = useQueryClient();

  const bulkStatus = async (ids, status, setBusy, clear) => {
    setBusy(true);
    let ok = 0, fail = 0;
    for (const id of ids) {
      try { await http.post(`/withdrawals/${id}/status`, { status }); ok++; } catch { fail++; }
    }
    setBusy(false); clear();
    qc.invalidateQueries({ queryKey: ["withdrawals"] });
    qc.invalidateQueries({ queryKey: ["balances"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    (fail ? toast.warning : toast.success)(`${ok} payout(s) → ${status}${fail ? `, ${fail} failed/locked` : ""}`);
  };

  const pmById = (id) => (opts.payment_methods || []).find((m) => m.id === id) || {};
  const myWallets = (wallets?.items || []).filter((w) => w.buyer_id === user.id).map((w) => {
    const pm = pmById(w.payment_method_id);
    return { ...w, method: w.method || pm.name || "—", network: w.network || pm.network || "", currency: w.currency || pm.currency || "—" };
  });
  const myPayouts = (withdrawals?.items || []).filter((w) => w.buyer_id === user.id);
  const allPayouts = withdrawals?.items || [];

  const TABS = staff ? [] : ["Payouts", "Wallets"];

  const walletCols = [
    { key: "label", label: "Label", render: (r) => <span className="font-medium text-foreground">{r.label || r.method || "—"}</span> },
    { key: "method", label: "Method", render: (r) => r.method || "—" },
    { key: "network", label: "Network", render: (r) => <span className="text-muted-foreground">{r.network || "—"}</span> },
    { key: "currency", label: "Currency" },
    { key: "address", label: "Address", mono: true, render: (r) => <span className="text-muted-foreground break-all">{r.address}</span> },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status || "active"} /> },
  ];
  const walletActions = (row) => [
    { label: "Edit", icon: Pencil, onClick: () => setWalletDrawer({ open: true, item: row }), testId: `edit-wallet-${row.id}` },
    { label: "Delete", icon: Trash2, danger: true, onClick: () => remove.mutate(row.id), testId: `delete-wallet-${row.id}` },
  ];
  const payoutCols = (withUser) => [
    { key: "order_no", label: "Order ID", mono: true, render: (r) => <span className="text-muted-foreground">{r.order_no || "—"}</span> },
    ...(withUser ? [{ key: "user_label", label: "User", sortable: true, render: (r) => <span className="font-medium text-foreground">{r.user_label || r.user_name}</span> }] : []),
    { key: "amount", label: "Amount", align: "right", mono: true, sortable: true, sortValue: (r) => Number(r.amount || 0), exportValue: (r) => r.amount, render: (r) => fmtMoney(r.amount) },
    { key: "method", label: "Method" },
    { key: "wallet", label: "Wallet", mono: true, render: (r) => <span className="text-muted-foreground break-all">{r.wallet}</span> },
    { key: "status", label: "Status", sortable: true, render: (r) => <StatusBadge status={r.status} /> },
    { key: "created_at", label: "Requested", sortable: true, render: (r) => fmtDate(r.created_at) },
  ];

  const payoutFilterFn = (w, f) => {
    if (f.dateFrom && (w.created_at || "") < f.dateFrom) return false;
    if (f.dateTo && (w.created_at || "") > f.dateTo + "T23:59:59") return false;
    return true;
  };
  const dateFilter = ({ values, set }) => (
    <div className="w-full sm:w-[190px]">
      <DateRangePicker from={values.dateFrom} to={values.dateTo}
        onChange={({ from, to }) => { set("dateFrom", from); set("dateTo", to); }} placeholder="Date range" testId="payout-date-range" />
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader title="Finance" subtitle="Your balances, payout wallets and withdrawal history." />

      <div className={cn("grid grid-cols-1 gap-4 sm:grid-cols-2", staff ? "lg:grid-cols-2" : "lg:grid-cols-4")}>
        <StatCard label="Hold Balance" value={fmtMoney(bal?.hold_balance)} icon={Lock} accent="#EAB308" testId="fin-hold-balance" />
        <StatCard label="Available Balance" value={fmtMoney(bal?.available_balance)} icon={Wallet} accent="#22C55E" testId="fin-available-balance" />
        {!staff && <StatCard label="Approved Payouts" value={fmtMoney(bal?.approved_payouts)} icon={ArrowDownUp} testId="fin-approved-total" />}
        {!staff && <StatCard label="Withdrawn Total" value={fmtMoney(bal?.withdrawn_total)} icon={Banknote} testId="fin-withdrawn-total" />}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 border-b border-white/[0.08]">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`finance-tab-${t.toLowerCase().replace(/ /g, "-")}`}
              className={cn("relative px-3 py-2 text-sm transition-colors", tab === t ? "text-foreground" : "text-muted-foreground hover:text-foreground")}>
              {t}{tab === t && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-white" />}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {!staff && <Button onClick={() => setWalletDrawer({ open: true, item: null })} data-testid="add-wallet-btn" className="h-9 gap-1.5 border border-white/15 bg-transparent text-foreground hover:bg-white/5"><Plus className="h-4 w-4" /> Add Wallet</Button>}
          <Button onClick={() => setWdOpen(true)} disabled={!(bal?.available_balance > 0)} data-testid="request-withdrawal-btn" className="h-9 gap-1.5 bg-[#22C55E] text-black hover:bg-[#16a34a] disabled:opacity-40"><Banknote className="h-4 w-4" /> Request Withdrawal</Button>
          {staff && <Button onClick={() => setPayoutOpen(true)} data-testid="create-payout-btn" className="h-9 gap-1.5 bg-white text-black hover:bg-white/90"><Send className="h-4 w-4" /> Create Payout</Button>}
        </div>
      </div>

      {!staff && tab === "Wallets" && <DataTablePro testId="wallets-table" columns={walletCols} rows={myWallets} loading={wLoading}
        searchKeys={["label", "method", "address", "currency"]} searchPlaceholder="Search wallets..." exportName="my-wallets"
        rowActions={walletActions} emptyText="No wallets yet — add one to receive payouts (pending staff approval)." />}

      {!staff && tab === "Payouts" && <DataTablePro testId="payouts-table" columns={payoutCols(false)} rows={myPayouts} loading={wdLoading}
        searchKeys={["order_no", "method", "wallet"]} searchPlaceholder="Search payouts..." exportName="my-payouts"
        filterFn={payoutFilterFn} customFilters={dateFilter}
        filters={[{ key: "status", label: "Status", getValue: (r) => r.status, options: asOpts(["new", "in_progress", "approved", "rejected"]) }]}
        rowActions={(row) => [{ label: "View details", icon: Eye, onClick: () => setReview(row), testId: `review-payout-${row.id}` }]}
        onRowClick={(r) => setReview(r)}
        emptyText="No withdrawal requests yet." />}

      {staff && <DataTablePro testId="payouts-table" columns={payoutCols(true)} rows={allPayouts} loading={wdLoading}
        searchKeys={["order_no", "user_name", "user_label", "user_email", "buyer_id", "method", "wallet"]} searchPlaceholder="Search by Order ID, user, User ID, method..." exportName="payouts"
        filterFn={payoutFilterFn} customFilters={dateFilter}
        filters={[
          { key: "buyer_id", label: "Buyer", multi: true, getValue: (r) => r.user_id || r.buyer_id, options: userOptions(opts.users) },
          { key: "status", label: "Status", getValue: (r) => r.status, options: asOpts([{ value: "new", label: "New" }, { value: "in_progress", label: "In Progress" }, { value: "approved", label: "Approved" }, { value: "rejected", label: "Rejected" }]) },
        ]}
        selectable bulkActions={[]}
        bulkExtra={({ ids, clear, busy, setBusy }) => (
          <>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => bulkStatus(ids, "in_progress", setBusy, clear)} data-testid="bulk-take-in-work" className="h-8 gap-1.5 border-white/[0.12] text-xs"><Loader2 className="h-3.5 w-3.5" /> Take in Work</Button>
            <Button size="sm" disabled={busy} onClick={() => bulkStatus(ids, "approved", setBusy, clear)} data-testid="bulk-approve-payouts" className="h-8 gap-1.5 bg-[#22C55E] text-xs text-black hover:bg-[#16a34a]"><Check className="h-3.5 w-3.5" /> Approve</Button>
            <Button size="sm" disabled={busy} onClick={() => bulkStatus(ids, "rejected", setBusy, clear)} data-testid="bulk-reject-payouts" className="h-8 gap-1.5 bg-[#EF4444] text-xs text-white hover:bg-[#dc2626]"><X className="h-3.5 w-3.5" /> Reject</Button>
          </>
        )}
        rowActions={(row) => [{ label: "Open / manage", icon: Eye, onClick: () => setReview(row), testId: `review-payout-${row.id}` }]}
        onRowClick={(r) => setReview(r)} emptyText="No payout requests in your scope." />}

      <WalletDrawer open={walletDrawer.open} item={walletDrawer.item} onOpenChange={(o) => setWalletDrawer((d) => ({ ...d, open: o }))} />
      <WithdrawFlow open={wdOpen} onOpenChange={setWdOpen} />
      {staff && <WithdrawalDrawer open={payoutOpen} item={null} admin onOpenChange={setPayoutOpen} />}
      {staff && <WithdrawalDrawer open={!!review} item={review} canApprove onOpenChange={(o) => !o && setReview(null)} />}
      {!staff && <WithdrawalDrawer open={!!review} item={review} onOpenChange={(o) => !o && setReview(null)} />}
    </div>
  );
}
